import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { FacetValue } from '@coveo/headless';
import { environment } from '@environments/environment';
import { Subscription } from 'rxjs';
import { GknRoutePath } from 'src/app/app-routing.enum';
import { RecentlyViewedService } from '../services/recently-viewed.service';

@Component({
    selector: 'app-recently-search-facet',
    templateUrl: './recently-search-facet.component.html',
    styleUrls: ['./recently-search-facet.component.scss'],
    imports: [CommonModule, MatIconModule, MatTooltipModule],
})
export class RecentlySearchFacetComponent implements OnInit, OnDestroy {
    private recentlyViewedService = inject(RecentlyViewedService);
    private router = inject(Router);

    @Input() title: string = 'Recently searched';
    @Input() maxItems: number = 10;
    /** void event that is fired after a search is submitted */
    @Output() afterSearch = new EventEmitter<void>();

    // private headlessFacet!: Facet;
    private recentlySearchedItems: string[] = [];
    public facetValues: FacetValue[] = [];
    private subscription: Subscription = new Subscription();

    ngOnInit(): void {
        this.loadRecentlySearchedItems();
        this.initializeController();

        // Subscribe to changes in recent searches
        this.subscription.add(
            this.recentlyViewedService.recentSearchesChanged$.subscribe(() => {
                this.loadRecentlySearchedItems();
                this.initializeController();
            }),
        );
    }

    /**
     * Unsubscribe from all subscriptions on destroy
     * @returns void
     */
    ngOnDestroy(): void {
        // Clean up subscriptions when component is destroyed
        this.subscription.unsubscribe();
    }

    /**
     * Load Recently Searched Items
     */
    private loadRecentlySearchedItems(): void {
        this.recentlySearchedItems = this.recentlyViewedService.recentSearches;
    }

    /**
     * Re-map recently searched items into facets
     */
    private initializeController(): void {
        // Convert recently viewed items to facet values
        this.facetValues = this.recentlySearchedItems.map((item: string) => ({
            value: item,
            numberOfResults: 1,
            state: 'idle' as const,
        }));
    }

    /**
     * Handle search term click and navigate to search page with the term.
     * Preserves existing query params like the product uid query.
     * If term is all numbers (case number), opens MySupport in new tab.
     * @param term - The search term to use
     */
    public onSearchTermClick(term: string): void {
        if (!term) return;

        // Check if the term is all numbers (a case number)
        if (/^\d+$/.test(term)) {
            // Open in new window with MySupport URL
            window.open(`${environment.mySupport.url}?case=${term}`, '_blank');
        } else {
            // Regular search term, navigate to search page
            this.router.navigate([`/${GknRoutePath.SEARCH}`], {
                queryParams: { q: term },
                queryParamsHandling: 'merge',
            });
        }

        // Emit event to notify parent component that search was performed
        this.afterSearch.emit();
    }

    /**
     * Show More Facet value options
     */
    public showMore(): void {
        this.maxItems += 10;
        this.loadRecentlySearchedItems();
        this.initializeController();
    }

    /**
     * Show Less Facet value options
     */
    public showLess(): void {
        this.maxItems = Math.max(10, this.maxItems - 10);
        this.loadRecentlySearchedItems();
        this.initializeController();
    }

    /**
     * Can Show Less Facets
     * @returns boolean
     */
    public canShowLess(): boolean {
        return this.maxItems > 10 && this.recentlySearchedItems.length > 10;
    }

    /**
     * Can Show More Facets
     * @returns boolean
     */
    public canShowMore(): boolean {
        return this.recentlySearchedItems.length > this.maxItems;
    }

    /**
     * Clear all recently viewed items
     */
    public clearSelections(): void {
        this.recentlyViewedService.clearAllSearches();
        this.recentlySearchedItems = [];
        this.facetValues = [];
    }
}
